// Packet_Summary rules and their computation (blueprint-sets Req 6, 5.7; design "sets/summary.ts").
// Deterministic and pure: no model call, nothing specific to one kind of packet. The browser computes the summary
// from a stored result, the run's snapshot of the rules, and the current confidence threshold, so a threshold change
// recomputes it without a run (Req 6.7).
import { canonicalJson } from '../preview.js';
import { isEmptyValue, isPossibleMismatch } from './coverage.js';
export const EMPTY_SUMMARY_RULES = Object.freeze({
    headerFields: [],
    rollups: [],
    checks: [],
});
export const MAX_HEADER_FIELDS = 20;
export const MAX_ROLLUPS = 30;
export const MAX_CHECKS = 20;
/** Slack added to tolerance comparisons, so values equal to two decimals compare equal. */
export const CHECK_EPSILON = 1e-9;
export const MISSING_FIELD_MESSAGE = 'Rule references a missing field.';
const SCALAR = new Set(['string', 'number', 'integer', 'boolean']);
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
function scalarType(p) {
    if (!isRecord(p))
        return null;
    if (typeof p.type === 'string' && SCALAR.has(p.type))
        return p.type;
    if (p.type === 'array' &&
        isRecord(p.items) &&
        typeof p.items.type === 'string' &&
        SCALAR.has(p.items.type) &&
        p.items.$ref === undefined)
        return `${p.items.type}[]`;
    return null;
}
function definitionOf(schema, ref) {
    if (typeof ref !== 'string')
        return null;
    const m = /^#\/definitions\/([^/]+)$/.exec(ref);
    const def = m && isRecord(schema.definitions) ? schema.definitions[m[1]] : undefined;
    return isRecord(def) && isRecord(def.properties) ? def.properties : null;
}
/** Every selectable path of a schema with its field type, for the rules editor and the validator. */
export function summaryPathsOf(schema) {
    const out = [];
    const props = isRecord(schema?.properties) ? schema.properties : {};
    for (const [name, prop] of Object.entries(props)) {
        if (!isRecord(prop))
            continue;
        const type = scalarType(prop);
        if (type) {
            out.push({ path: name, type });
            continue;
        }
        if (prop.$ref !== undefined) {
            for (const [sub, p] of Object.entries(definitionOf(schema, prop.$ref) ?? {})) {
                const t = scalarType(p);
                if (t)
                    out.push({ path: `${name}.${sub}`, type: t });
            }
            continue;
        }
        if (prop.type === 'array' && isRecord(prop.items) && prop.items.$ref !== undefined) {
            for (const [col, p] of Object.entries(definitionOf(schema, prop.items.$ref) ?? {})) {
                const t = scalarType(p);
                if (t)
                    out.push({ path: `${name}[].${col}`, type: t, table: name });
            }
        }
    }
    return out;
}
const isNumericType = (t) => t === 'number' || t === 'integer';
const isPathSource = (s) => !('segments' in s);
const pathsByMember = (members) => new Map(members.map((m) => [m.blueprintId, new Map(summaryPathsOf(m.schema).map((p) => [p.path, p]))]));
/** Reference and type problems of one Header_Field (null when it resolves). */
function headerProblem(h, schemas) {
    const paths = schemas.get(h.source.memberId);
    if (!paths)
        return 'This blueprint is not in the set.';
    const p = paths.get(h.source.path);
    if (!p)
        return MISSING_FIELD_MESSAGE;
    if (p.table)
        return 'A header field cannot come from a table column. Use a rollup instead.';
    return null;
}
/** Reference and type problems of one Rollup, keyed `sources.<j>` or `groupBy` (empty when it resolves). */
function rollupProblems(r, schemas) {
    const out = {};
    if (r.sources.length === 0)
        out.sources = 'Add at least one source.';
    const tables = new Set();
    r.sources.forEach((s, j) => {
        const key = `sources.${j}`;
        const paths = schemas.get(s.memberId);
        if (!paths) {
            out[key] = 'This blueprint is not in the set.';
            return;
        }
        if (!isPathSource(s)) {
            if (r.aggregate !== 'count')
                out[key] = 'Only a count can count documents.';
            tables.add(undefined);
            return;
        }
        const p = paths.get(s.path);
        if (!p) {
            out[key] = MISSING_FIELD_MESSAGE;
            return;
        }
        if (r.aggregate !== 'count' && !isNumericType(p.type))
            out[key] = `A ${r.aggregate} needs a number field.`;
        tables.add(p.table);
    });
    const g = r.groupBy;
    if (g?.kind === 'field') {
        // Gap 4: grouping by a field needs every source in that one Member; a table column groups per row only within
        // its own table, a scalar or group field groups per Segment.
        const memberIds = new Set(r.sources.map((s) => s.memberId));
        const paths = memberIds.size === 1 ? schemas.get([...memberIds][0]) : undefined;
        const p = paths?.get(g.path);
        if (memberIds.size !== 1)
            out.groupBy = 'Group by a field only when every source is from the same blueprint. Group by blueprint instead.';
        else if (!paths)
            out.groupBy = 'This blueprint is not in the set.';
        else if (!p)
            out.groupBy = MISSING_FIELD_MESSAGE;
        else if (p.type !== 'string')
            out.groupBy = 'Group by a text field.';
        else if (p.table && [...tables].some((t) => t !== p.table))
            out.groupBy = `Group by a column of ${p.table} only when every source is a column of ${p.table}.`;
    }
    return out;
}
/** Reference problems of one operand (null when it resolves). */
function operandProblem(o, rules, schemas) {
    if (o.kind === 'header') {
        const h = rules.headerFields.find((x) => x.id === o.id);
        if (!h)
            return 'This header field no longer exists.';
        const p = schemas.get(h.source.memberId)?.get(h.source.path);
        if (p && !isNumericType(p.type))
            return 'Compare number fields only.';
        return null;
    }
    const r = rules.rollups.find((x) => x.id === o.id);
    if (!r)
        return 'This rollup no longer exists.';
    if (o.kind === 'rollupGroup') {
        if (!r.groupBy)
            return 'This rollup has no groups.';
        if (o.group.trim().length === 0)
            return 'Choose a group.';
    }
    return null;
}
function nameProblems(list, nameOf, prefix, field, errors) {
    const seen = new Set();
    list.forEach((item, i) => {
        const name = nameOf(item).trim();
        if (name.length === 0)
            errors[`${prefix}.${i}.${field}`] = 'Enter a name.';
        else if (name.length > 128)
            errors[`${prefix}.${i}.${field}`] = 'Keep the name to 128 characters.';
        else if (seen.has(name.toLowerCase()))
            errors[`${prefix}.${i}.${field}`] = `"${name}" is used twice.`;
        seen.add(name.toLowerCase());
    });
}
function idProblems(list, prefix, errors) {
    const seen = new Set();
    list.forEach((item, i) => {
        if (item.id.trim().length === 0 || seen.has(item.id))
            errors[`${prefix}.${i}.id`] = 'Every rule needs its own id.';
        seen.add(item.id);
    });
}
/**
 * Req 6.2: references, numeric-ness (type `number`/`integer` from the Member's schema at its ref), grouping (gap 4),
 * blank and duplicate names, limits. Keys: `headerFields`, `headerFields.<i>.label|source|id`,
 * `rollups.<i>.name|sources|sources.<j>|groupBy|id`, `checks.<i>.name|left|right|tolerance|id`, `rollups`, `checks`.
 */
export function validateSummaryRules(rules, members) {
    const errors = {};
    const schemas = pathsByMember(members);
    if (rules.headerFields.length > MAX_HEADER_FIELDS)
        errors.headerFields = `A set can have at most ${MAX_HEADER_FIELDS} header fields.`;
    if (rules.rollups.length > MAX_ROLLUPS)
        errors.rollups = `A set can have at most ${MAX_ROLLUPS} rollups.`;
    if (rules.checks.length > MAX_CHECKS)
        errors.checks = `A set can have at most ${MAX_CHECKS} checks.`;
    idProblems(rules.headerFields, 'headerFields', errors);
    idProblems(rules.rollups, 'rollups', errors);
    idProblems(rules.checks, 'checks', errors);
    nameProblems(rules.headerFields, (h) => h.label, 'headerFields', 'label', errors);
    nameProblems(rules.rollups, (r) => r.name, 'rollups', 'name', errors);
    nameProblems(rules.checks, (c) => c.name, 'checks', 'name', errors);
    rules.headerFields.forEach((h, i) => {
        const p = headerProblem(h, schemas);
        if (p)
            errors[`headerFields.${i}.source`] = p;
    });
    rules.rollups.forEach((r, i) => {
        for (const [k, v] of Object.entries(rollupProblems(r, schemas)))
            errors[`rollups.${i}.${k}`] = v;
    });
    rules.checks.forEach((c, i) => {
        const l = operandProblem(c.left, rules, schemas);
        if (l)
            errors[`checks.${i}.left`] = l;
        const r = operandProblem(c.right, rules, schemas);
        if (r)
            errors[`checks.${i}.right`] = r;
        if (!Number.isFinite(c.tolerance.value) || c.tolerance.value < 0)
            errors[`checks.${i}.tolerance`] = 'The tolerance must be zero or more.';
    });
    return errors;
}
/** No value: null, missing, blank text, or an empty list (defined with Field_Coverage, re-exported here). */
export { isEmptyValue };
const groupLabel = (v) => isEmptyValue(v) ? '' : Array.isArray(v) ? v.join('; ') : String(v);
function splitTablePath(path) {
    const i = path.indexOf('[].');
    return i < 0 ? null : { table: path.slice(0, i), column: path.slice(i + 3) };
}
/** Every value of `path` in a Segment: one for a field (absent → undefined), one per row for a table column. */
function readPath(s, path) {
    const t = splitTablePath(path);
    if (!t) {
        const f = s.fields.find((x) => x.path === path);
        return [{ segmentIndex: s.index, path, value: f?.value, confidence: f?.confidence ?? null }];
    }
    const table = s.tables.find((x) => x.name === t.table);
    return (table?.rows ?? []).map((r, row) => ({
        segmentIndex: s.index,
        path,
        row,
        value: r[t.column]?.value,
        confidence: r[t.column]?.confidence ?? null,
    }));
}
const isLow = (confidence, threshold) => confidence !== null && confidence < threshold;
function inputOf(r, threshold) {
    const reason = isEmptyValue(r.value) ? 'MISSING' : isLow(r.confidence, threshold) ? 'LOW_CONFIDENCE' : null;
    if (!reason)
        return null;
    return { segmentIndex: r.segmentIndex, path: r.path, ...(r.row !== undefined ? { row: r.row } : {}), reason };
}
function aggregate(kind, values) {
    if (kind === 'count')
        return values.length;
    if (values.length === 0)
        return null;
    if (kind === 'sum')
        return values.reduce((a, b) => a + b, 0);
    return kind === 'min' ? Math.min(...values) : Math.max(...values);
}
/**
 * Deterministic (Req 6.10). `members[i]` is the snapshot Member that `segment.matchedMember === i` refers to; a
 * Segment with no (or an unknown) Matched_Member is a No_Match_Segment, ignored by every rule (Req 6.8).
 *
 * - Header_Field: the non-empty value with the highest confidence over the Segments matched to its Member (ties: lowest
 *   segment index; a null confidence ranks lowest), how many Segments had one, and whether they differ (Req 6.5).
 * - Rollup: every source value in every Segment matched to the source's Member (table columns: every row), empty and
 *   non-numeric values skipped; `count` counts non-empty values (or Segments for a `segments` source); `used` is the
 *   number of values aggregated. A sum, min, or max of no values is null. Missing and low-confidence values are listed
 *   in `inputs`.
 * - Check: `left - right` against the tolerance (absolute, or percent of `|right|`) plus `1e-9`; `UNVERIFIED` when an
 *   operand is null or any value it depends on is missing or below `threshold` (Req 6.6), with those values in
 *   `inputs`.
 * - A rule whose reference no longer resolves in its Member's schema is returned with `invalid: true` and no value
 *   (Req 6.3); a check on an invalid rule is invalid too.
 * - A matched Segment for which `isPossibleMismatch(segment, coverageFloor)` holds and whose index is not in
 *   `acceptedSegments` is treated like a No_Match_Segment by every rule and counted in `possibleMismatchSegments` and
 *   `possibleMismatchPages`; an accepted one counts as matched (Req 5.10, 6.8). A check is `UNVERIFIED` when a Member
 *   one of its operands reads matched at least one Segment and every such Segment is excluded; those Segments are
 *   listed in `inputs` with reason `POSSIBLE_MISMATCH`. Omitted or 0 `coverageFloor`: no exclusion.
 */
export function computePacketSummary(args) {
    const { rules, members, threshold } = args;
    const floor = args.coverageFloor ?? 0;
    const accepted = args.acceptedSegments;
    const schemas = pathsByMember(members);
    const segments = [...args.segments].sort((a, b) => a.index - b.index);
    /** The Member BDA matched, whether or not the Segment is excluded. */
    const matchedMemberOf = (s) => {
        const i = s.matchedMember;
        return i === null || i === undefined ? null : (members[i]?.blueprintId ?? null);
    };
    const isExcluded = (s) => matchedMemberOf(s) !== null && isPossibleMismatch(s, floor) && !(accepted?.has(s.index) ?? false);
    /** The Member the rules read the Segment as: null for No_Match and excluded Possible_Mismatch_Segments. */
    const memberOf = (s) => (isExcluded(s) ? null : matchedMemberOf(s));
    const matched = (memberId) => segments.filter((s) => memberOf(s) === memberId);
    const noMatch = segments.filter((s) => matchedMemberOf(s) === null);
    const excluded = segments.filter(isExcluded);
    const pagesOf = (list) => list.reduce((n, s) => n + Math.max(0, s.pageEnd - s.pageStart + 1), 0);
    /** POSSIBLE_MISMATCH inputs of one source: its Member's Segments when every one of them is excluded. */
    const mismatchInputs = (memberId, path) => {
        const all = segments.filter((s) => matchedMemberOf(s) === memberId);
        if (all.length === 0 || !all.every(isExcluded))
            return [];
        return all.map((s) => ({ segmentIndex: s.index, path, reason: 'POSSIBLE_MISMATCH' }));
    };
    /** Every source an operand reads (through its Header_Field or Rollup). */
    const operandMismatch = (o) => {
        if (o.kind === 'header') {
            const h = rules.headerFields.find((x) => x.id === o.id);
            return h ? mismatchInputs(h.source.memberId, h.source.path) : [];
        }
        const r = rules.rollups.find((x) => x.id === o.id);
        return r ? r.sources.flatMap((src) => mismatchInputs(src.memberId, isPathSource(src) ? src.path : '')) : [];
    };
    const headerInputs = new Map();
    const headers = rules.headerFields.map((h) => {
        if (headerProblem(h, schemas)) {
            headerInputs.set(h.id, []);
            return {
                id: h.id,
                label: h.label,
                value: null,
                confidence: null,
                segments: 0,
                differs: false,
                invalid: true,
            };
        }
        const reads = matched(h.source.memberId).flatMap((s) => readPath(s, h.source.path));
        const present = reads.filter((r) => !isEmptyValue(r.value));
        let best;
        for (const r of present)
            if (!best || (r.confidence ?? -Infinity) > (best.confidence ?? -Infinity))
                best = r;
        const distinct = new Set(present.map((r) => canonicalJson(r.value)));
        const input = best ? inputOf(best, threshold) : null;
        headerInputs.set(h.id, best ? (input ? [input] : []) : reads.flatMap((r) => inputOf(r, threshold) ?? []));
        return {
            id: h.id,
            label: h.label,
            value: best ? best.value : null,
            confidence: best ? best.confidence : null,
            segments: present.length,
            differs: distinct.size > 1,
        };
    });
    /** Per Rollup: every input with its group, for check operands on one group. */
    const rollupReads = new Map();
    const rollups = rules.rollups.map((r) => {
        if (Object.keys(rollupProblems(r, schemas)).length > 0) {
            rollupReads.set(r.id, []);
            return { id: r.id, name: r.name, value: null, used: 0, inputs: [], invalid: true };
        }
        const values = [];
        const inputs = [];
        const groupOrder = [];
        const noteGroup = (g) => {
            if (g !== undefined && !groupOrder.includes(g))
                groupOrder.push(g);
        };
        const gb = r.groupBy;
        const groupColumn = gb?.kind === 'field' ? splitTablePath(gb.path) : null;
        for (const source of r.sources) {
            for (const s of matched(source.memberId)) {
                const segGroup = gb?.kind === 'member'
                    ? source.memberId
                    : gb?.kind === 'field' && !groupColumn
                        ? groupLabel(s.fields.find((f) => f.path === gb.path)?.value)
                        : undefined;
                if (!isPathSource(source)) {
                    noteGroup(segGroup);
                    values.push({ value: 1, ...(segGroup !== undefined ? { group: segGroup } : {}) });
                    continue;
                }
                for (const read of readPath(s, source.path)) {
                    const group = groupColumn && read.row !== undefined
                        ? groupLabel(s.tables.find((t) => t.name === groupColumn.table)?.rows[read.row]?.[groupColumn.column]?.value)
                        : segGroup;
                    noteGroup(group);
                    const input = inputOf(read, threshold);
                    if (input)
                        inputs.push({ ...input, ...(group !== undefined ? { group } : {}) });
                    if (isEmptyValue(read.value))
                        continue;
                    if (r.aggregate === 'count')
                        values.push({ value: 1, ...(group !== undefined ? { group } : {}) });
                    else if (typeof read.value === 'number' && Number.isFinite(read.value))
                        values.push({ value: read.value, ...(group !== undefined ? { group } : {}) });
                }
            }
        }
        rollupReads.set(r.id, inputs);
        const out = {
            id: r.id,
            name: r.name,
            value: aggregate(r.aggregate, values.map((v) => v.value)),
            used: values.length,
            inputs: inputs.map(({ group: _g, ...i }) => i),
        };
        if (gb) {
            out.groups = groupOrder.map((g) => {
                const vs = values.filter((v) => v.group === g).map((v) => v.value);
                return { group: g, value: aggregate(r.aggregate, vs), used: vs.length };
            });
        }
        return out;
    });
    const operand = (o) => {
        if (o.kind === 'header') {
            const h = headers.find((x) => x.id === o.id);
            if (!h || h.invalid)
                return { value: null, inputs: [], invalid: true };
            return {
                value: typeof h.value === 'number' && Number.isFinite(h.value) ? h.value : null,
                inputs: headerInputs.get(o.id) ?? [],
                invalid: false,
            };
        }
        const r = rollups.find((x) => x.id === o.id);
        if (!r || r.invalid)
            return { value: null, inputs: [], invalid: true };
        const reads = rollupReads.get(o.id) ?? [];
        if (o.kind === 'rollup')
            return { value: r.value, inputs: r.inputs, invalid: false };
        if (!r.groups)
            return { value: null, inputs: [], invalid: true };
        return {
            value: r.groups.find((g) => g.group === o.group)?.value ?? null,
            inputs: reads.filter((i) => i.group === o.group).map(({ group: _g, ...i }) => i),
            invalid: false,
        };
    };
    const checks = rules.checks.map((c) => {
        const l = operand(c.left);
        const r = operand(c.right);
        const resolves = !operandProblem(c.left, rules, schemas) && !operandProblem(c.right, rules, schemas);
        if (l.invalid || r.invalid || !resolves) {
            return {
                id: c.id,
                name: c.name,
                left: null,
                right: null,
                difference: null,
                status: 'UNVERIFIED',
                inputs: [],
                invalid: true,
            };
        }
        const inputs = dedupeInputs([...l.inputs, ...r.inputs, ...operandMismatch(c.left), ...operandMismatch(c.right)]);
        const difference = l.value !== null && r.value !== null ? l.value - r.value : null;
        let status = 'UNVERIFIED';
        if (difference !== null && inputs.length === 0) {
            const tol = (c.tolerance.kind === 'percent' ? (c.tolerance.value * Math.abs(r.value)) / 100 : c.tolerance.value) +
                CHECK_EPSILON;
            const ok = c.op === 'eq' ? Math.abs(difference) <= tol : c.op === 'lte' ? difference <= tol : difference >= -tol;
            status = ok ? 'PASS' : 'FAIL';
        }
        return { id: c.id, name: c.name, left: l.value, right: r.value, difference, status, inputs };
    });
    return {
        headers,
        rollups,
        checks,
        noMatchSegments: noMatch.length,
        noMatchPages: pagesOf(noMatch),
        possibleMismatchSegments: excluded.length,
        possibleMismatchPages: pagesOf(excluded),
    };
}
function dedupeInputs(inputs) {
    const seen = new Set();
    return inputs.filter((i) => {
        const k = `${i.segmentIndex}|${i.path}|${i.row ?? ''}`;
        if (seen.has(k))
            return false;
        seen.add(k);
        return true;
    });
}
//# sourceMappingURL=summary.js.map